import React from 'react';
import { SharedDialog } from '../SharedDialog';
import { useGlobalError } from './ErrorContext';

/**
 * Global Error Dialog - displays any global errors as a prominent modal dialog.
 * Always mounted so Abyss ModalDialog can transition from closed → open.
 * isAuthError=true  → mode="error"   (red, not dismissible, Re-authenticate button)
 * isAuthError=false → mode="confirm" (yellow warning, dismissible, Dismiss button)
 * EXTERNAL hard-timeout is a special case: EXTERNAL users have no Azure account to
 * re-authenticate with, so they get a single "Close" action that attempts window.close()
 * instead of Re-authenticate/Dismiss-to-signed-out.
 */
export const GlobalErrorDialog = () => {
  const { error, clearError, handleReauth } = useGlobalError();
  const isOpen = !!error;
  const isAuthError = error?.isAuthError ?? false;
  const isHardTimeout = error?.reason === 'hard-timeout';
  const isExchangeFailure = error?.reason === 'exchange-failure';
  const isExternalHardTimeout = isHardTimeout && error?.authType === 'EXTERNAL';

  const handleCloseTab = React.useCallback(() => {
    if (isExchangeFailure || isExternalHardTimeout) {
      window.close();
      return;
    }

    clearError();
    if (isHardTimeout) {
      window.history.replaceState({}, document.title, '/signed-out');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
    window.close();
  }, [clearError, isExchangeFailure, isExternalHardTimeout, isHardTimeout]);

  return (
    <SharedDialog
      isOpen={isOpen}
      onClose={isExchangeFailure ? () => {} : clearError}
      mode={isAuthError ? 'error' : 'confirm'}
      title={error?.title ?? ''}
      description={error?.message}
      ariaLabel={error?.title ?? 'Notification'}
      dismissible={!isAuthError}
      secondaryAction={
        isAuthError && !isExchangeFailure && !isExternalHardTimeout
          ? {
              label: 'Re-authenticate',
              onClick: handleReauth,
            }
          : undefined
      }
      primaryAction={{
        label: isExchangeFailure ? 'Close tab' : isExternalHardTimeout ? 'Close' : 'Dismiss',
        onClick: isAuthError ? handleCloseTab : clearError,
      }}
      initialFocus={isExchangeFailure || isExternalHardTimeout ? 'primary' : isAuthError ? 'secondary' : 'primary'}
    />
  );
};
